import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

const STAGE_COLORS = {
  "Inbound Calls": "#3F3F46",
  "Inspection Scheduled": "#52525B",
  "Quote Sent": "#A1A1AA",
  "Job In Progress": "#E8E8E8",
  "Completed": "#dc2626",
};

export default function DashboardSales() {
  const [data, setData] = useState({ funnel: [], deals: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/sales");
        setData(data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  return (
    <div className="space-y-6" data-testid="sales-page">
      <div>
        <div className="font-mono-apex text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-2">module · jobs &amp; dispatch</div>
        <h1 className="font-display text-4xl text-white tracking-tight">
          <em className="silver-text not-italic">Crews</em> as a system.
        </h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Funnel */}
        <div className="lg:col-span-2 bg-[#121214] border border-white/5 p-5">
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">conversion funnel</div>
          <div className="font-display text-xl text-white mb-5">Volume · stage by stage</div>          <div className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.funnel} layout="vertical" margin={{ top: 0, right: 30, left: 30, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.04)" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#71717A", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="stage" tick={{ fill: "#a1a1aa", fontSize: 11, fontFamily: "JetBrains Mono" }} axisLine={false} tickLine={false} width={100} />
                <Tooltip
                  cursor={{ fill: "rgba(255,255,255,0.03)" }}
                  contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", fontFamily: "JetBrains Mono", fontSize: 11 }}
                  itemStyle={{ color: "#fff" }}
                />
                <Bar dataKey="value" radius={[0, 0, 0, 0]} fill="#dc2626">
                  {data.funnel.map((d, i) => (
                    <rect key={i} fill={STAGE_COLORS[d.stage] || "#dc2626"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top deal scorecards */}
        <div className="bg-[#121214] border border-white/5 p-5">
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">conversion summary</div>
          <div className="font-display text-xl text-white mb-5">From call → completed</div>
          <div className="space-y-4">
            {data.funnel.map((s, i) => {
              const total = data.funnel[0]?.value || 1;
              const pct = (s.value / total) * 100;
              return (
                <div key={i}>
                  <div className="flex justify-between font-mono-apex text-[11px] mb-1">
                    <span className="text-zinc-300">{s.stage}</span>
                    <span className="text-white">{s.value.toLocaleString()}</span>
                  </div>
                  <div className="h-1 bg-white/5">
                    <div
                      className="h-full"
                      style={{
                        width: `${pct}%`,
                        background: STAGE_COLORS[s.stage],
                      }}
                    />
                  </div>
                  <div className="font-mono-apex text-[9px] text-zinc-600 mt-1">{pct.toFixed(1)}% of inbound</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Deals table */}
      <div className="bg-[#121214] border border-white/5">
        <div className="px-5 pt-5">
          <div className="font-mono-apex text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">priority jobs</div>
          <div className="font-display text-xl text-white mb-4">Top jobs · ranked by Sentinel score</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full font-mono-apex text-xs">
            <thead>
              <tr className="text-zinc-500 uppercase tracking-[0.2em] text-[10px] border-y border-white/5">
                <th className="text-left px-5 py-3 font-normal">Job · Site</th>
                <th className="text-left px-5 py-3 font-normal">Stage</th>
                <th className="text-left px-5 py-3 font-normal">Value</th>
                <th className="text-left px-5 py-3 font-normal">Crew</th>
                <th className="text-right px-5 py-3 font-normal">Sentinel Score</th>
              </tr>
            </thead>
            <tbody>
              {data.deals.map((d, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3 text-white">{d.company}</td>
                  <td className="px-5 py-3 text-zinc-300">{d.stage}</td>
                  <td className="px-5 py-3 text-zinc-300">{d.value}</td>
                  <td className="px-5 py-3 text-zinc-400">{d.owner}</td>
                  <td className="px-5 py-3 text-right">
                    <span className={`px-2 py-1 ${d.score >= 90 ? "bg-[#dc2626]/15 text-[#fca5a5]" : d.score >= 80 ? "bg-emerald-500/10 text-emerald-300" : "bg-white/5 text-zinc-300"}`}>
                      {d.score}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
